import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const MainPage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [inputData, setInputData] = useState([]);
  const [layers, setLayers] = useState([]);
  const [algorithmSteps, setAlgorithmSteps] = useState([]);
  const [aiFits, setAiFits] = useState([]);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 100 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setInputData(allItems.filter(item => item.type === 'platform_input'));
        setLayers(allItems.filter(item => item.type === 'platform_layer'));
        setAlgorithmSteps(allItems.filter(item => item.type === 'algorithm_step'));
        setAiFits(allItems.filter(item => item.type === 'ai_fit')); 
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>The Platform - My Website</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 36 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-3">
            {parse(cms[36]?.cms_page_heading || "Understanding Biology. <span>Not Guessing It.</span>")}
          </h2>
          {parse(cms[36]?.cms_page_description || "")}
          <a href="#" className="action-items-primary-btn mr-2">
            {cms[36]?.cms_page_small_description || "Explore Predyxiq IQ"}
            <i className="fas fa-chevron-right" />
          </a>
          <a href="#" className="action-items-primary-btn">
            {cms[36]?.cms_page_small_description_second || "Request a Technical Briefing"}
            <i className="fas fa-chevron-right" />
          </a>

          <img
            src={`${apiBaseUrl}/assets/images/${cms[36]?.cms_image}`}
            alt="About us"
            className="img-fluid"
            style={{ marginTop: "20px" }}
          />
        </div>
      </section>

      {/* ============================================ */}
      {/* INTELLIGENCE BEHIND Predyxiq - CMS ID 37 */}
      {/* ============================================ */}
      <section className="predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[37]?.cms_page_heading || "The Intelligence <span>Behind Predyx</span>")}
            </h2>
            <h4 className="predyx-sub-title mt-2">
              {parse(cms[37]?.cms_page_small_description || "Built on Clinical Intelligence. <span> Not Artificial Intelligence.</span>")}
            </h4>
          </div>

          <div className="predyx-content-box">
            {parse(cms[37]?.cms_page_description || "")}
          </div>

          <div className="predyx-process-section mt-5">
            <h3 className="predyx-process-title text-center">
              {cms[38]?.cms_page_heading || "The Predyx Algorithm Process"}
            </h3>
            <p className="predyx-process-subtitle text-center">
              {cms[38]?.cms_page_small_description || "Every assessment passes through four sequential stages of deterministic reasoning."}
            </p>

            <div className="row">
              {algorithmSteps.map((item, index) => (
                <div className="col-md-6 mb-4" key={index}>
                  <div className="predyx-step-card">
                    <div className="predyx-step-number">0{index + 1}</div>
                    <div className="predyx-step-content">
                      <h5>{item.title}</h5>
                      <p>{parse(item.description || '')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="predyx-quote-section mt-3">
            <blockquote>
              “Clinical Intelligence is not generated. It is systematically
              derived through deterministic medical reasoning.”
            </blockquote>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHAT ENTERS THE ALGORITHM - CMS ID 39, 40, 41 */}
      {/* ============================================ */}
      <section className="predyx2-section">
        <div className="container">
          <div className="predyx2-heading text-center mb-4">
            <h2 className="predyx2-title mt-2">
              {parse(cms[39]?.cms_page_heading || "What Enters the <span>Predyx Algorithm</span>")}
            </h2>
            <h4 className="predyx2-subtitle mt-2">
              {cms[39]?.cms_page_small_description || "Clinical Intelligence begins with structured observation."}
            </h4>
            <p className="predyx2-description mt-4">
              {parse(cms[39]?.cms_page_description || "The Predyx Clinical Intelligence Algorithm is intentionally modality-agnostic. It integrates information from every major domain of routine healthcare into one unified physiological framework.")}
            </p>
          </div>

          <div className="row">
            {inputData.map((item, index) => (
              <div className="col-lg-6 mb-4" key={index}>
                <div className="predyx2-card">
                  <div className="predyx2-card-number">0{index + 1}</div>
                  <div className="predyx2-card-content">
                    <h5>{item.title}</h5>
                    <p>{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="predyx2-future">
            <div className="row align-items-center">
              <div className="col-md-2 text-center">
                <div className="predyx2-future-icon">⬈</div>
              </div>
              <div className="col-md-10">
                <h4>{cms[40]?.cms_page_heading || "Future Digital Inputs"}</h4>
                <p className="mb-0">{parse(cms[40]?.cms_page_description || "The architecture is designed to evolve. As healthcare advances, the algorithm can incorporate digital biomarkers, remote monitoring, wearable technologies, home diagnostics and future clinical innovations without changing its underlying reasoning framework.")}</p>
              </div>
            </div>
          </div>

          <div className="predyx2-bottom-box mt-4">
            <h3>{cms[41]?.cms_page_heading || "Predyx is not built around tests."}</h3>
            <p className="mb-0">{cms[41]?.cms_page_small_description || "It is built around biology."}</p>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOUR LAYERS - CMS ID 42, 43 */}
      {/* ============================================ */}
      <section className="predyx3-section">
        <div className="container">
          <div className="predyx3-header text-center">
            <h2 className="predyx3-title mt-2">
              {parse(cms[42]?.cms_page_heading || "Four Layers of <span>Clinical Intelligence</span>")}
            </h2>
            <p className="predyx3-description mt-2">
              {cms[42]?.cms_page_small_description || "The Predyx Algorithm evaluates every assessment through four sequential reasoning layers."}
            </p>
          </div>

          <div className="predyx3-timeline mt-5">
            {layers.map((item, index) => (
              <div className="predyx3-item" key={index}>
                <div className="predyx3-left">
                  <div className="predyx3-circle">0{index + 1}</div>
                  {index !== layers.length - 1 && <span className="predyx3-line"></span>}
                </div>
                <div className="predyx3-card">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="predyx3-footer mt-5">
            <h3>{cms[43]?.cms_page_heading || "We are not replacing clinical judgement."}</h3>
            <p>{cms[43]?.cms_page_small_description || "We are organising it."}</p>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PLATFORM ARCHITECTURE - CMS ID 44 */}
      {/* ============================================ */}
    <section className="predyx4-section">
        <div className="container">
          <div className="predyx4-heading text-center mb-5">
            <h2 className="predyx4-title">
            {parse(cms[44]?.cms_page_heading || '')}
            
            </h2>

            <h4 className="predyx4-subtitle"> {cms[44]?.cms_page_small_description}</h4>
          </div>

          <div className="predyx4-flow">
            <div className="predyx4-item">
              <div className="predyx4-box">Clinical Information</div>
              <div className="predyx4-arrow">&#8595;</div>
            </div>

            <div className="predyx4-item">
              <div className="predyx4-box">
                Proprietary Predyx Clinical Intelligence Algorithm
                <span>(Reasoning Layer)</span>
              </div>
              <div className="predyx4-arrow">&#8595;</div>
            </div>

            <div className="predyx4-item">
              <div className="predyx4-box">
                Predyx
                <span>
                  (Communication, Workflow, Reporting, Visualization Layer)
                </span>
              </div>
              <div className="predyx4-arrow">&#8595;</div>
            </div>

            <div className="predyx4-item">
              <div className="predyx4-box">Physician</div>
              <div className="predyx4-arrow">&#8595;</div>
            </div>

            <div className="predyx4-item">
              <div className="predyx4-box">Patient</div>
            </div>
          </div>

          <div className="predyx4-note mt-5">
            Predyx performs the clinical reasoning. Predyx delivers the
            clinical experience. The separation is deliberate. The separation is
            fundamental.
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* EXPLAINABILITY - CMS ID 45, 46, 47, 48, 49, 50 */}
      {/* ============================================ */}
      <section className="explain-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h2 className="section-title">{cms[45]?.cms_page_heading || "Explainability"}</h2>
              <p className="section-tag">{cms[45]?.cms_page_small_description || "EVERY RECOMMENDATION SURVIVES ONE QUESTION"}</p>
              <h3 className="hero-title">{cms[45]?.cms_page_small_description_second || '"Doctor... why?"'}</h3>
            </div>
          </div>

          <div className="decision-card">
            <div className="decision-header">
              <div className="status-dot" />
              <h4>{cms[46]?.cms_page_heading || "Clinical Warning"}</h4>
              <span>{cms[46]?.cms_page_small_description || "Progressive Cardiometabolic Trajectory Detected"}</span>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <div className="info-card">
                  <h5>Evidence Used</h5>
                  {parse(cms[46]?.cms_page_description || "")}
                </div>
              </div>

              <div className="col-lg-6">
                <div className="analysis-card">
                  <div className="analysis-block">
                    <h6>{cms[47]?.cms_page_heading || "Clinical Interpretation"}</h6>
                    <p>{cms[47]?.cms_page_small_description || "Click to Expand physiological mapping"}</p>
                  </div>
                  <div className="analysis-block">
                    <h6>{cms[48]?.cms_page_heading || "Supporting Physiological Relationships & Evidence Base"}</h6>
                    <p>{cms[48]?.cms_page_small_description || "Click to Expand peer-reviewed source literature"}</p>
                  </div>
                  <div className="analysis-block">
                    <h6>{cms[49]?.cms_page_heading || "Suggested Clinical Actions"}</h6>
                    <p>{cms[49]?.cms_page_small_description || "Click to Expand structured pathway guidelines"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="predyx4-note mt-5">
            {cms[50]?.cms_page_heading || "Every recommendation expands. Nothing remains hidden. Medicine advances through understanding. Trust begins through explanation."}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHERE AI FITS - CMS ID 51, 52 */}
      {/* ============================================ */}
      <section className="predyx6-section">
        <div className="container">
          <div className="predyx6-header mb-5">
            <h2 className="predyx6-title text-center">
              {parse(cms[51]?.cms_page_heading || "Where Artificial <span>Intelligence Fits</span>")}
            </h2>
          </div>

          <div className="predyx6-card">
      {aiFits.map((item, index) => (
        <React.Fragment key={index}>
          <div className="predyx6-item">
            <div className="predyx6-count">{item.title}</div>
            <div className="predyx6-content">
              <p>{item.description}</p>
            </div>
          </div>
          {index < aiFits.length - 1 && <div className="predyx6-divider"></div>}
        </React.Fragment>
      ))}
    </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HEALTH IS A TRAJECTORY - CMS ID 53 */}
      {/* ============================================ */}



      <section className="predyx7-section py-5">
        <div className="container">
          <div className="predyx7-header mb-5">
            <h2 className="predyx7-title text-center">
            {parse(cms[53]?.cms_page_heading || "")}
            </h2>
          </div>

          <div className="predyx7-card">
            <p className="predyx7-text">
            {parse(cms[53]?.cms_page_small_description || "")}
            </p>

            <p className="predyx7-text mb-5">
            {parse(cms[53]?.cms_page_description || "")}
            </p>

            <div className="predyx7-flow">
              <div className="predyx7-step">Assessment 1</div>

              <div className="predyx7-arrow">&#10140;</div>

              <div className="predyx7-step predyx7-active">Assessment 2</div>

              <div className="predyx7-arrow">&#10140;</div>

              <div className="predyx7-step">Assessment 3</div>
            </div>

            <div className="predyx7-down-arrow">&#8595;</div>

            <div className="predyx7-trajectory">
              Trajectory:
              <span> [ Improving | Stable | Progressing ]</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 54 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "35px", color: "#fff", fontWeight: "600" }}>
              {parse(cms[54]?.cms_page_heading || "")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {cms[54]?.cms_page_small_description || ""}
            </p>
            <a href="#" className="action-items-primary-btn1">
              Explore Predyxiq IQ Quick
              <i className="fas fa-chevron-right" />
            </a>
            <a href="#" className="action-items-primary-btn1">
              Request a Technical Briefing
              <i className="fas fa-chevron-right" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainPage;